// components/feed/MultiDepartmentSection.js
import React from 'react';
import { Container, Row, Col, Carousel, Button, Spinner } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import SpecializedFeedSection from './SpecializedFeedSection';
import MemberCard from '../members/MemberCard';
import useMemberData from '../../hooks/useMemberData';
import { MEMBER_TYPES } from '../../utils/membersConfig';

/**
 * MultiDepartmentSection - Handles multiple departments in rows (like Executive branch)
 * 
 * @param {Object} props
 * @param {Array} props.departments - Array of department configurations
 * @param {number} props.departmentsPerRow - Number of departments per row (default: 2)
 * @param {number} props.carouselInterval - Carousel interval timing
 * @param {number} props.maxItemsPerFeed - Max items per feed
 */
const MultiDepartmentSection = ({
  departments = [],
  departmentsPerRow = 2,
  carouselInterval = 5000,
  maxItemsPerFeed = 1
}) => {
  // Group departments into rows
  const departmentRows = [];
  for (let i = 0; i < departments.length; i += departmentsPerRow) {
    departmentRows.push(departments.slice(i, i + departmentsPerRow));
  }

  return (
    <>
      {departmentRows.map((row, rowIndex) => (
        <div key={`dept-row-${rowIndex}`} id='branchPage'>
          <Container className='bgcolor'>
            <Container id='carouselBufferTwo'>
              <Row>
                {row.map((department, colIndex) => (
                  <Col 
                    key={department.id} 
                    id={colIndex % 2 === 0 ? 'legislativeColLeft' : 'legislativeColRight'}
                  >
                    <DepartmentCarousel
                      departmentConfig={department}
                      carouselInterval={carouselInterval}
                      maxItemsPerFeed={maxItemsPerFeed}
                      columnClass={colIndex % 2 === 0 ? 'columnLefttPadding' : 'columnRightPadding'}
                    />
                  </Col>
                ))}
                
                {/* Add empty column if odd number of departments in row */}
                {row.length === 1 && (
                  <Col id='legislativeColRight'>
                    <div id='columnRightPadding'></div>
                  </Col>
                )}
              </Row>
            </Container>
          </Container>
        </div>
      ))}
    </>
  );
};

/**
 * Helper function to convert memberType string to MEMBER_TYPES constant
 */
const getMemberType = (memberTypeString) => {
  switch (memberTypeString) {
    case 'senate':
      return MEMBER_TYPES.SENATE;
    case 'house':
      return MEMBER_TYPES.HOUSE;
    case 'president':
      return MEMBER_TYPES.PRESIDENT;
    case 'justice':
      return MEMBER_TYPES.JUSTICE;
    default:
      return MEMBER_TYPES.HOUSE;
  }
};

/**
 * MemberCarouselItem - Renders a single member in the carousel
 * Fetches random member data and displays using MemberCard
 */
const MemberCarouselItem = ({ memberType, title }) => {
  const { data, loading, error } = useMemberData(getMemberType(memberType), 1);

  if (loading) {
    return (
      <div><Spinner id='spinner' animation="grow" variant="light"  /></div>
 
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: 'white'
      }}>
        No member data available
      </div>
    );
  }

  return (
    <MemberCard
      member={data[0]}
      memberType={getMemberType(memberType)}
      variant="full"
      containerId="feedContainerNormalView"
    />
  );
};

/**
 * Individual Department Carousel (used within MultiDepartmentSection)
 * Now supports member items alongside regular feed items
 */
const DepartmentCarousel = ({
  departmentConfig,
  carouselInterval,
  maxItemsPerFeed,
  columnClass
}) => {
  const {
    departmentTitle,
    departmentLink,
    carouselItems = []
  } = departmentConfig;

  // Separate different item types
  const feedItems = carouselItems.filter(item => 
    item.type !== 'viewall' && item.type !== 'member'
  );
  const memberItems = carouselItems.filter(item => item.type === 'member');
  const viewAllItem = carouselItems.find(item => item.type === 'viewall');

  // Combine all carousel slides: members first, then feeds
  const allCarouselItems = [
    ...memberItems.map(item => ({ ...item, itemType: 'member' })),
    ...feedItems.map(item => ({ ...item, itemType: 'feed' }))
  ];

  return (
    <div className={columnClass}>
<h4
  id='branchHeading' 
  onClick={(e) => {
    e.preventDefault();
    window.open(departmentLink, "_self");
  }}
  style={{ cursor: 'pointer' }}
>
  <b id="branchHeadingBold">{departmentTitle}</b>
  <ChevronRight id='clickChevron' />
</h4>

      <Carousel id='carouselInColumn' controls={false} interval={carouselInterval}>
        {/* All Carousel Items (members and feeds) */}
        {allCarouselItems.map((item, index) => (
          <Carousel.Item key={item.id || `item-${index}`} interval={carouselInterval}>
            {item.itemType === 'member' ? (
              <MemberCarouselItem 
                memberType={item.memberType}
                title={item.title}
              />
            ) : (
              <SpecializedFeedSection
                feedType={item.feedType}
                title={item.title}
                apiEndpoint={item.apiEndpoint}
                feedName={item.feedName}
                branch={item.branch}
                topic={item.topic}
                buttonName={item.buttonName}
                maxItems={maxItemsPerFeed}
              />
            )}
          </Carousel.Item>
        ))}

        {/* View All Item */}
        {viewAllItem && (
          <Carousel.Item interval={carouselInterval}>
            <div id='legislativeConInCol'>
              <div id='viewFullPageButton'>
                <Button 
                  id='viewFullLegislativePageButton' 
                  size='lg' 
                  href={viewAllItem.buttonLink}
                >
                  {viewAllItem.buttonText}
                </Button>
              </div>
            </div>
          </Carousel.Item>
        )}
      </Carousel>
    </div>
  );
};

export default MultiDepartmentSection;